import React, { useEffect, useRef } from 'react';
import PropTypes, { any } from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { ICONS } from 'constants/icons';
import { Button, Eyebrow, Headline, SectionDivider } from 'components/atoms';
import { get } from 'lodash';
import {
  CtaBanner,
  FeatureCard,
  Figure,
  Footnotes,
  Markdown,
  Quote,
  VideoFeatureCard,
} from 'components/molecules';
import { black, white } from 'styles/colors';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import * as Styled from './Overlay.styled';
import Gallery from '../gallery/Gallery';

/**
 * Renders trend details overlay
 * @param {Object} data
 * @param {boolean} isOpen
 * @param {string|undefined} previousTitle
 * @param {Function} handlePrevious
 * @param {string|undefined} nextTitle
 * @param {Function} handleNext
 * @param {Function} handleClose
 * @returns {ReactElement}
 */
const Overlay = ({
  data,
  isOpen,
  previousTitle,
  handlePrevious,
  nextTitle,
  handleNext,
  handleClose,
}) => {
  // Scroll lock handling
  const overlayRef = useRef(null);
  const scrollableRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const overlayElement = overlayRef.current;
    const scrollableElement = scrollableRef.current;

    if (!overlayElement || !scrollableElement) return undefined;

    disableBodyScroll(scrollableElement, {
      reserveScrollBarGap: true,
      allowTouchMove: el => {
        return el === scrollableElement || scrollableElement.contains(el);
      },
    });

    // Cleanup function
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [isOpen]); // Only re-run if isOpen changes

  if (!isOpen || !data) return null;

  // get data from props
  const { title, eyebrow, backgroundColor, textColor } = data;

  // get modules from data
  const modules = get(data, 'modulesCollection.items', []);
  const renderModule = module => {
    // eslint-disable-next-line no-underscore-dangle
    switch (module.__typename) {
      case 'ModuleMarkdownText':
        return (
          <Styled.ModuleContainer>
            <Markdown body={module.markdownBody} />
          </Styled.ModuleContainer>
        );
      case 'ModuleImage':
        return (
          <Styled.ModuleContainer>
            <Figure data={module} type="image" className="image-module" />
          </Styled.ModuleContainer>
        );
      case 'ModuleFeatureCard':
        return <FeatureCard {...module} fullWidth />;
      case 'ModuleSectionDivider':
        return <SectionDivider />;
      case 'CtaBanner':
        return <CtaBanner {...module} />;
      case 'ModuleFootnotes':
        return <Footnotes {...module} textColor={textColor || black} />;
      case 'ModuleQuote':
        return (
          <Quote {...module} textColor={textColor || black} centered={false} />
        );
      case 'ModuleVideoFeatureCard':
        return (
          <VideoFeatureCard
            {...module}
            eyebrow={module.eyebrow}
            video={{
              videoId: module.moduleVideo?.videoId || '',
              videoName: module.moduleVideo?.name || '',
              type: module.moduleVideo?.type || 'Vimeo',
              verticalVideo: module.moduleVideo?.verticalVideo || { url: '' },
              autoplay: module.moduleVideo?.autoplay || false,
            }}
          />
        );
      case 'ModuleGallery':
        return (
          <Gallery
            title={module.title}
            columns={module.columns}
            rows={module.rows}
            items={get(module, 'itemsCollection.items', [])}
            backgroundColor={backgroundColor || white}
            textColor={textColor || black}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider
      theme={{
        backgroundColor: backgroundColor || white,
        textColor: textColor || black,
      }}
    >
      <Styled.OverlayBackground onClick={handleClose} ref={overlayRef}>
        <Styled.MainContainer>
          <Styled.ButtonContainer>
            <Button type="roundClose" onClick={handleClose} />
          </Styled.ButtonContainer>
          <Styled.OverlayContainer
            onClick={e => e.stopPropagation()}
            ref={scrollableRef}
          >
            <Styled.Navigation $onlyNext={!previousTitle}>
              {previousTitle && (
                <Styled.NavigationItem onClick={handlePrevious}>
                  <Styled.Arrow name={ICONS.ARROW_LEFT_NO_CONTAINER} />
                  {previousTitle.split('__').join('')}
                </Styled.NavigationItem>
              )}
              {nextTitle && (
                <Styled.NavigationItem onClick={handleNext}>
                  {nextTitle.split('__').join('')}
                  <Styled.Arrow name={ICONS.ARROW_RIGHT_NO_CONTAINER} />
                </Styled.NavigationItem>
              )}
            </Styled.Navigation>
            <Styled.TitleContainer>
              {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
              <Headline styling="h1" text={title} />
            </Styled.TitleContainer>
            {modules.map(module => (
              <Styled.Modules key={module.sys?.id}>
                {renderModule(module)}
              </Styled.Modules>
            ))}
          </Styled.OverlayContainer>
        </Styled.MainContainer>
      </Styled.OverlayBackground>
    </ThemeProvider>
  );
};

Overlay.propTypes = {
  /**
   * The overlay data
   */
  data: PropTypes.objectOf(any),
  /**
   * Whether the overlay is open
   */
  isOpen: PropTypes.bool,
  /**
   * index of the element
   */
  index: PropTypes.number,
  /**
   * title of previous element or undefined
   */
  previousTitle: PropTypes.string,
  /**
   * handler for navigating to previous element
   */
  handlePrevious: PropTypes.func,
  /**
   * title of next element or undefined
   */
  nextTitle: PropTypes.string,
  /**
   * handler for navigating to next element
   */
  handleNext: PropTypes.func,
  /**
   * handler for closing overlay
   */
  handleClose: PropTypes.func,
};

export default Overlay;
